import { faSitemap } from "@fortawesome/free-solid-svg-icons/faSitemap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Popover from "@mui/material/Popover";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Tag } from "../../stores/definitions/Tag";
import DescriptionList from "../DescriptionList";
import IconButtonWithTooltip from "../IconButtonWithTooltip";

type TagListingArgs = {
  tags: Array<Tag>;
  size?: "small" | "medium";

  /*
   * Additional content may be added inline to the end of the listing, such as
   * a button to add more tags. The content MUST be wrapped in a <Grid>
   */
  endAdornment?: React.ReactNode;

  showMetadataPopup?: boolean;
  onDelete?: (index: number, tag: Tag) => void;
  onClick?: (tag: Tag) => void;
};

/**
 * This component provides a listing of tags, with the ability to delete tags
 * if `onDelete` is true, to show the associated metadata if
 * `showMetadataPopup` is true, and to perform an action when a tag is clicked
 * if `onClick` is provided.
 */
export default function TagListing({
  tags,
  size = "medium",
  endAdornment,
  showMetadataPopup = true,
  onDelete,
  onClick = () => {},
}: TagListingArgs): React.ReactNode {
  const { t } = useTranslation("common");
  const [metadataPopup, setMetadataPopup] = useState<{
    anchorEl: HTMLElement;
    tag: Tag;
  } | null>(null);
  const cardContent = (tag: Tag) => [
    {
      label: t("tags.metadata.term"),
      value: tag.value,
    },
    {
      label: t("tags.metadata.termUri"),
      value: tag.uri.orElse("—"),
    },
    {
      label: t("tags.metadata.controlledVocabularyName"),
      value: tag.vocabulary.orElse("—"),
    },
    {
      label: t("tags.metadata.controlledVocabularyVersion"),
      value: tag.version.orElse("—"),
    },
  ];

  return (
    <Grid container direction="row" spacing={1}>
      {tags.map((tag, index) => (
        <Grid key={tag.uri.orElse(tag.value)}>
          <Chip
            label={tag.value}
            size={size}
            /*
             * When the chip is a clickable link to performing a search, we
             * set the `a` and `href` props so that the user can right-click
             * the chip and open it in a new tab/copy the link to the tag
             * search.
             */
            component={onDelete ? "div" : "a"}
            variant={
              tag.uri.isPresent() && tag.version.isPresent() && tag.vocabulary.isPresent() ? "filled" : "outlined"
            }
            sx={{
              /*
               * By unsetting the userSelect style, when the chip is right
               * clicked the browser's context menu provides the option of
               * copying the link text, which is to say the tag's value.
               */
              userSelect: "unset",
              cursor: onDelete ? "default" : "pointer",
            }}
            {...(onDelete
              ? { onDelete: () => onDelete(index, tag) }
              : {
                  href: `/inventory/search?query=l: (tags:"${tag.value}")`,
                  onClick: (e: React.MouseEvent<HTMLDivElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClick(tag);
                  },
                })}
            {...(showMetadataPopup &&
              tag.uri.isPresent() &&
              tag.vocabulary.isPresent() &&
              tag.version.isPresent() && {
                icon: (
                  <IconButtonWithTooltip
                    title={t("tags.metadata.view")}
                    icon={<FontAwesomeIcon icon={faSitemap} />}
                    size="small"
                    color="primary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMetadataPopup({
                        anchorEl: e.currentTarget,
                        tag,
                      });
                    }}
                  />
                ),
              })}
          />
        </Grid>
      ))}
      {metadataPopup && showMetadataPopup && (
        <Popover
          open={true}
          anchorEl={metadataPopup.anchorEl}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          onClose={() => {
            setMetadataPopup(null);
          }}
        >
          <Card>
            <CardContent>
              <DescriptionList content={cardContent(metadataPopup.tag)} dividers={true} />
            </CardContent>
          </Card>
        </Popover>
      )}
      {endAdornment}
    </Grid>
  );
}
